import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import leftArrowIcon from '@assets/icons/placement-page/left-arrow.svg';
import rightArrowIcon from '@assets/icons/placement-page/right-arrow.svg';
import firstplaced from '@assets/icons/placement-page/first-placed.png';
import secondplaced from '@assets/icons/placement-page/second-placed.png';
import thirdplaced from '@assets/icons/placement-page/third-placed.png';
import fourthplaced from '@assets/icons/placement-page/fourth-placed.png';
import amazonLogo from '@assets/icons/placement-page/amazon-logo.svg';
import flower from '@assets/icons/placement-page/flower.svg';
import { COURSE_TYPE_NAMES_MAPPING } from '@lib/constants';

const TestimonialSlider = ({
  testimonialsdummy,
  scrollContainerRef,
  activeMonth,
  setActiveMonth,
  monthIndexMap,
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const isManualMonthChange = useRef(false);
  const scrollInProgress = useRef(false);

  const transformTestimonials = (apiData) => {
    const seen = new Set();

    return apiData
      .filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      })
      .map((item) => ({
        id: item.id,
        name: item.Name,
        position: item.Role,
        quote: item.HeroReview || item.Review,
        image: item.Image?.data?.attributes?.url || firstplaced,
        companyLogo: null, // Add your own logic or default logo if available
        tags: item.Tags ? item.Tags.split(',') : [],
      }));
  };

  const testimonials = [
    {
      id: 1,
      name: 'Charu Khan',
      position: 'Data Associate',
      quote: 'I want to extend my heartfelt thanks to the placement team.',
      image: firstplaced,
      companyLogo: amazonLogo,
      tags: ['Web Development', 'Tech', 'Working Professional'],
    },
    {
      id: 2,
      name: 'Pratik Shinde',
      position: 'Sr. Associate',
      quote: 'A heartfelt thank you to AlmaBetter & my mentors for their constant guidance.',
      image: secondplaced,
      companyLogo: amazonLogo,
      tags: ['Web Development', 'Tech', 'Working Professional'],
    },
    {
      id: 3,
      name: 'Aiman',
      position: 'Sr. Associate',
      quote: 'The most crucial skill for any coder is logic building.',
      image: thirdplaced,
      companyLogo: amazonLogo,
      tags: ['Web Development', 'Tech', 'Working Professional'],
    },
    {
      id: 4,
      name: 'Ashish K Jha',
      position: 'Sr. Associate',
      quote: 'A heartfelt thank you to AlmaBetter & my mentors for their constant guidance.',
      image: fourthplaced,
      companyLogo: amazonLogo,
      tags: ['Web Development', 'Tech', 'Working Professional'],
    },
    {
      id: 5,
      name: 'Ashish K Jha',
      position: 'Sr. Associate',
      quote: 'A heartfelt thank you to AlmaBetter & my mentors for their constant guidance.',
      image: fourthplaced,
      companyLogo: amazonLogo,
      tags: ['Web Development', 'Tech', 'Working Professional'],
    },
  ];

  useEffect(() => {
    const updateWidth = () => {
      if (scrollContainerRef.current) {
        setContainerWidth(scrollContainerRef.current.offsetWidth);
      }
    };

    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [scrollContainerRef]);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current || isManualMonthChange.current || scrollInProgress.current)
        return;

      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      let closestCard = null;
      let minDistance = Infinity;

      Array.from(container.children).forEach((card) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - containerCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestCard = card;
        }
      });

      if (closestCard) {
        const cardIndex = Array.from(container.children).indexOf(closestCard);
        const visibleTestimonial = testimonialsdummy[cardIndex];

        if (
          visibleTestimonial &&
          visibleTestimonial.month &&
          visibleTestimonial.month !== activeMonth
        ) {
          setActiveMonth(visibleTestimonial.month);
        }
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [scrollContainerRef, testimonialsdummy, activeMonth, setActiveMonth]);

  // Handle manual month changes
  useEffect(() => {
    if (!activeMonth || !scrollContainerRef.current) return;

    isManualMonthChange.current = true;
    scrollInProgress.current = true;

    // Find testimonials for the selected month
    const monthTestimonials = testimonialsdummy.filter(
      (testimonial) => testimonial.month === activeMonth
    );

    if (monthTestimonials.length > 0) {
      const firstMonthIndex = testimonialsdummy.indexOf(monthTestimonials[0]);
      const cardWidth = scrollContainerRef.current.children[0]?.offsetWidth || 0;
      const gap = 16; // gap-4 in Tailwind
      const scrollPosition = firstMonthIndex * (cardWidth + gap);

      scrollContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }

    // Reset flags after scrolling completes
    const timer = setTimeout(() => {
      isManualMonthChange.current = false;
      scrollInProgress.current = false;
    }, 800);

    return () => clearTimeout(timer);
  }, [activeMonth, testimonialsdummy]);

  const scroll = (direction) => {
    if (!scrollContainerRef.current || scrollInProgress.current) return;

    const scrollDistance = Math.max(200, containerWidth / 3);
    scrollInProgress.current = true;

    scrollContainerRef.current.scrollBy({
      left: direction === 'left' ? -scrollDistance : scrollDistance,
      behavior: 'smooth',
    });

    // Update active month after scrolling
    setTimeout(() => {
      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      let closestCard = null;
      let minDistance = Infinity;

      Array.from(container.children).forEach((card) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - containerCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestCard = card;
        }
      });

      if (closestCard) {
        const cardIndex = Array.from(container.children).indexOf(closestCard);
        const visibleTestimonial = testimonialsdummy[cardIndex];

        if (visibleTestimonial?.month && visibleTestimonial.month !== activeMonth) {
          setActiveMonth(visibleTestimonial.month);
        }
      }

      scrollInProgress.current = false;
    }, 800);
  };

  return (
    <div className="flex flex-col items-center p-[5px]">
      <div className="relative mt-0 w-full max-w-[1280px]">
        <button
          onClick={() => scroll('left')}
          className="absolute -left-20 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#F00037] bg-white transition-opacity hover:opacity-75 md:block xl:flex"
          aria-label="Previous testimonial"
        >
          <Image src={leftArrowIcon} width={20} height={20} alt="Previous" className="text-[#F00037]" />
        </button>
        <div className="w-full">
          <div
            ref={scrollContainerRef}
            className="scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-visible pt-6 pb-8 md:max-w-full md:overflow-x-scroll md:pt-7"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {testimonialsdummy?.map((testimonial) => (
              <div
                key={testimonial.id}
                className="group max-w-[80%] flex-shrink-0 cursor-pointer sm:min-w-[260px] sm:max-w-[260px] md:min-w-[290px] md:max-w-[285px]"
              >
                <div className="custom-rounded-gradient-placement relative mb-0 h-[194px] overflow-hidden transition-all duration-300">
                  <div className="absolute left-12 -bottom-2 z-10">
                    <Image
                      className="transition-transform duration-500 group-hover:rotate-90"
                      src={flower}
                      alt="Flower"
                      width={50}
                      height={63}
                    />
                  </div>

                  {testimonial?.image && (
                    <div className="absolute bottom-0 right-[-10px] flex h-[200px] w-[160px] items-end overflow-hidden">
                      <Image
                        src={testimonial?.image}
                        alt={testimonial.name}
                        width={160}
                        height={200}
                        className="h-[200px] w-[160px] object-contain"
                      />
                    </div>
                  )}

                  <div className="absolute top-4 left-4 flex max-w-[180px] flex-col items-start gap-1.5">
                    {' '}
                    {/* Increased max width */}
                    {testimonial.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block rounded-[4px] bg-white px-2 py-0.5 font-proxima-nova text-[12px] font-medium text-[#1F1F1F] group-hover:bg-opacity-90" // Increased font size to 12px
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Content below image */}
                <div className="custom-rounded-placement mt-0 flex h-[180px] flex-col px-3 py-4 sm:px-4">
                  <div className="flex flex-col gap-2">
                    <h3 className="text-left font-proxima-nova text-[16px] font-medium leading-[150%] text-[#1F1F1F] line-clamp-1">
                      {testimonial.name}
                    </h3>
                    <p className="text-left font-proxima-nova text-[14px] font-medium leading-[150%] text-[#C9903D] line-clamp-1">
                      {testimonial.position}
                    </p>
                    <p className="self-stretch font-proxima-nova text-[12px] font-normal leading-[150%] text-[#484848] line-clamp-2">
                      {testimonial.quote}
                    </p>
                  </div>

                  {/* Company Logo Row pinned to bottom */}
                  <div className="mt-auto flex items-center justify-between pt-2">
                    <div className="mr-3 font-proxima-nova text-[12px] font-normal leading-[150%] text-[#A1A1A1]">
                      Placed at:
                    </div>
                    {testimonial?.companyLogo && (
                      <div className="h-[48px] w-[80px] md:h-[50px] md:w-[140px] flex justify-end">
                        <Image
                          src={testimonial.companyLogo}
                          alt="Company logo"
                          width={140}
                          height={56}
                          className="object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => scroll('right')}
          className="absolute -right-20 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#F00037] bg-white shadow-[0px_2px_16px_0px_rgba(0,0,0,0.08)] transition-opacity hover:opacity-75 xl:flex"
          aria-label="Next testimonial"
        >
          <Image src={rightArrowIcon} width={20} height={20} alt="Next" />
        </button>
      </div>
    </div>
  );
};

export default TestimonialSlider;
